import { orderBy } from 'lodash';
import React from 'react';

import { Tooltip } from '@dndbeyond/character-common-components/es';
import {
    BoxBackground,
    FancyBoxSvg230x200,
    FancyBoxSvg281x200,
    SavingThrowsSummary,
} from '@dndbeyond/character-components/es';
import {
    Ability,
    CharacterTheme,
    CharacterUtils,
    Constants,
    DeathSaveInfo,
    DiceAdjustment,
    RuleData,
    SituationalSavingThrowInfoLookup,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';

import DiceAdjustmentSummary from '../../../Shared/components/DiceAdjustmentSummary';
import { StyleSizeTypeEnum } from '../../../Shared/reducers/appEnv';
import { AppEnvDimensionsState } from '../../../Shared/stores/typings';

interface Props {
    abilities: Array<Ability>;
    ruleData: RuleData;
    savingThrowDiceAdjustments: Array<DiceAdjustment>;
    situationalBonusSavingThrowsLookup: SituationalSavingThrowInfoLookup;
    deathSaveInfo: DeathSaveInfo;
    maxSummariesShown: number;
    onAbilityClick?: (ability: Ability) => void;
    onInfoClick?: () => void;
    onClick?: () => void;
    dimensions: AppEnvDimensionsState;
    theme: CharacterTheme;
    diceEnabled: boolean;
    rollContext: IRollContext;
}
export default class SavingThrowsBox extends React.PureComponent<Props> {
    static defaultProps = {
        maxSummariesShown: 3,
        diceEnabled: false,
    };

    filterDiceAdjustments = (): Array<DiceAdjustment> => {
        const { savingThrowDiceAdjustments, maxSummariesShown, deathSaveInfo } = this.props;

        const advantageSavingThrowAdjustments: Array<DiceAdjustment> = [];
        const disadvantageSavingThrowAdjustments: Array<DiceAdjustment> = [];
        const bonusSavingThrowAdjustments: Array<DiceAdjustment> = [];

        savingThrowDiceAdjustments.forEach((adjustment) => {
            if (adjustment.type === Constants.DiceAdjustmentTypeEnum.ADVANTAGE) {
                advantageSavingThrowAdjustments.push(adjustment);
            }
            if (adjustment.type === Constants.DiceAdjustmentTypeEnum.DISADVANTAGE) {
                disadvantageSavingThrowAdjustments.push(adjustment);
            }
            if (adjustment.type === Constants.DiceAdjustmentTypeEnum.BONUS) {
                bonusSavingThrowAdjustments.push(adjustment);
            }
        });

        //get highest dice adjustment count from all saving throws
        let highestSavingThrowAdjustmentCount: number = Math.max(
            advantageSavingThrowAdjustments.length,
            disadvantageSavingThrowAdjustments.length,
            bonusSavingThrowAdjustments.length,
        );

        //get highest dice adjustment count from all dice adjustments
        let highestDiceAdjustmentCount: number = Math.max(
            highestSavingThrowAdjustmentCount,
            deathSaveInfo.advantageAdjustments.length,
            deathSaveInfo.disadvantageAdjustments.length,
        );

        let diceAdjustments: Array<DiceAdjustment> = [];
        if (highestDiceAdjustmentCount) {
            let saveAdjustmentMaxLoopCount: number = Math.min(maxSummariesShown, highestSavingThrowAdjustmentCount);

            //Does a round robin of all saving throw adjustments
            for (let i = 0; i < saveAdjustmentMaxLoopCount; i++) {
                if (diceAdjustments.length >= maxSummariesShown) {
                    continue;
                }
                if (advantageSavingThrowAdjustments.length > i) {
                    diceAdjustments.push(advantageSavingThrowAdjustments[i]);
                }
                if (diceAdjustments.length < maxSummariesShown && disadvantageSavingThrowAdjustments.length > i) {
                    diceAdjustments.push(disadvantageSavingThrowAdjustments[i]);
                }
                if (diceAdjustments.length < maxSummariesShown && bonusSavingThrowAdjustments.length > i) {
                    diceAdjustments.push(bonusSavingThrowAdjustments[i]);
                }
            }

            //Does a round robin of any death save adjustments if there is room in the diceAdjustments array
            let deathSaveMaxLoopCount: number = Math.max(0, maxSummariesShown - diceAdjustments.length);
            for (let i = 0; i < deathSaveMaxLoopCount; i++) {
                if (diceAdjustments.length >= maxSummariesShown) {
                    continue;
                }
                if (deathSaveInfo.advantageAdjustments.length > i) {
                    diceAdjustments.push(deathSaveInfo.advantageAdjustments[i]);
                }

                if (diceAdjustments.length < maxSummariesShown && deathSaveInfo.disadvantageAdjustments.length > i) {
                    diceAdjustments.push(deathSaveInfo.disadvantageAdjustments[i]);
                }
            }

            if (diceAdjustments.length > 1) {
                return this.sortDiceAdjustments(diceAdjustments);
            }
        }

        return diceAdjustments;
    };

    sortDiceAdjustments = (diceAdjustments: Array<DiceAdjustment>): Array<DiceAdjustment> => {
        let sortOrderLookup: Record<string, number> = {
            [Constants.DiceAdjustmentTypeEnum.ADVANTAGE]: 1,
            [Constants.DiceAdjustmentTypeEnum.DISADVANTAGE]: 2,
            [Constants.DiceAdjustmentTypeEnum.BONUS]: 3,
        };
        return orderBy(diceAdjustments, [(diceAdjustment) => sortOrderLookup[diceAdjustment.type]]);
    };

    handleAbilityClick = (ability: Ability): void => {
        const { onAbilityClick } = this.props;

        if (onAbilityClick) {
            onAbilityClick(ability);
        }
    };

    handleClick = (evt: React.MouseEvent): void => {
        const { onClick } = this.props;

        if (onClick) {
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();
            onClick();
        }
    };

    handleInfoClick = (evt: React.MouseEvent): void => {
        const { onInfoClick } = this.props;

        if (onInfoClick) {
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();
            onInfoClick();
        }
    };

    renderSummary = (diceAdjustment: DiceAdjustment, includeTooltip: boolean = true): React.ReactNode => {
        const { ruleData, theme } = this.props;

        const tooltip = CharacterUtils.generateSavingThrowAdjustmentSummary(diceAdjustment, ruleData);

        // White space is very intentional
        return (
            <Tooltip
                title={tooltip}
                enabled={includeTooltip}
                className="ct-saving-throws-box__modifier"
                key={diceAdjustment.uniqueKey}
                isDarkMode={theme.isDarkMode}
            >
                <DiceAdjustmentSummary
                    diceAdjustment={diceAdjustment}
                    ruleData={ruleData}
                    showDataOrigin={false}
                    theme={theme}
                />
            </Tooltip>
        );
        //`
    };

    renderSingleSummary = (diceAdjustment: DiceAdjustment): React.ReactNode => {
        const { theme } = this.props;
        return (
            <div
                className={`ct-saving-throws-box__modifiers ct-saving-throws-box__modifiers--single ${
                    theme.isDarkMode ? 'ct-saving-throws-box__modifiers--dark-mode' : ''
                }`}
            >
                {this.renderSummary(diceAdjustment, false)}
            </div>
        );
    };

    renderMultiSummary = (diceAdjustments: Array<DiceAdjustment>): React.ReactNode => {
        const { theme } = this.props;

        return (
            <div
                className={`ct-saving-throws-box__modifiers ct-saving-throws-box__modifiers--multi ${
                    theme.isDarkMode ? 'ct-saving-throws-box__modifiers--dark-mode' : ''
                }`}
            >
                {diceAdjustments.map((diceAdjustment) => this.renderSummary(diceAdjustment))}
            </div>
        );
    };

    renderEmpty = (): React.ReactNode => {
        const { theme } = this.props;

        return (
            <div
                className={`ct-saving-throws-box__modifiers ct-saving-throws-box__modifiers--empty ${
                    theme.isDarkMode ? 'ct-saving-throws-box__modifiers--dark-mode' : ''
                }`}
            >
                Saving Throw Modifiers
            </div>
        );
    };

    renderSaveInfo = (): React.ReactNode => {
        const diceAdjustments = this.filterDiceAdjustments();

        if (diceAdjustments.length === 0) {
            return this.renderEmpty();
        }
        if (diceAdjustments.length === 1) {
            return this.renderSingleSummary(diceAdjustments[0]);
        }

        return this.renderMultiSummary(diceAdjustments);
    };

    render() {
        const { abilities, situationalBonusSavingThrowsLookup, dimensions, theme, diceEnabled, rollContext } =
            this.props;

        let BoxBackgroundComponent: React.ComponentType = FancyBoxSvg230x200;
        let useSmallRowStyle: boolean = true;
        if (
            dimensions.styleSizeType > StyleSizeTypeEnum.DESKTOP ||
            dimensions.styleSizeType <= StyleSizeTypeEnum.TABLET
        ) {
            BoxBackgroundComponent = FancyBoxSvg281x200;
            useSmallRowStyle = false;
        }

        return (
            <div className="ct-saving-throws-box" onClick={this.handleClick}>
                <BoxBackground StyleComponent={BoxBackgroundComponent} theme={theme} />
                <div className="ct-saving-throws-box__abilities">
                    <SavingThrowsSummary
                        abilities={abilities}
                        situationalBonusSavingThrowsLookup={situationalBonusSavingThrowsLookup}
                        onClick={this.handleAbilityClick}
                        diceEnabled={diceEnabled}
                        theme={theme}
                        rowStyle={useSmallRowStyle ? 'small' : 'normal'}
                        rollContext={rollContext}
                    />
                </div>
                <div className="ct-saving-throws-box__info" onClick={this.handleInfoClick}>
                    {this.renderSaveInfo()}
                </div>
            </div>
        );
    }
}
